import React from "react";
import { Button } from "@mui/material";
import { Order, Reservation, OfficeConfig, ContractorConfig } from "@types";

import ClientInformationForm from "./ClientInformationForm";
import Appointment from "./Appointment";
import OrderRecapDetails from "components/OrderRecap/OrderRecapDetails";
import ReservationCancelled from "components/Reservation/ReservationCancelled";
import ReservationDetails from "components/Reservation/ReservationDetails";
import ReservationExpired from "components/Reservation/ReservationExpired";
import ReservationUsed from "components/Reservation/ReservationUsed";
import {
  DeliveryFlowStep,
  DeliveryFlowTab,
  DELIVERY_FLOW_STEP,
} from "../deliveryFlow";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

interface TabNavigationProps {
  currentIndex: number;
  setActiveTab: (value: DeliveryFlowStep) => void;
  tabs: DeliveryFlowTab[];
  isNextDisabled: boolean;
}

const TabNavigation = ({
  currentIndex,
  setActiveTab,
  tabs,
  isNextDisabled,
}: TabNavigationProps) => {
  const lastTabIndex = tabs.length - 1;

  return (
    <div className="tab-navigation">
      <div className="tab-navigation_container">
        <span className="tab-navigation__prev">
          <Button
            disabled={currentIndex === 0}
            variant="outlined"
            color="primary"
            startIcon={<NavigateBeforeIcon />}
            onClick={() => setActiveTab(tabs[currentIndex - 1].id)}
          >
            précédent
          </Button>
        </span>
        <span className="tab-navigation__next">
          <Button
            disabled={currentIndex === lastTabIndex || isNextDisabled}
            variant="outlined"
            color="primary"
            endIcon={<NavigateNextIcon fontSize="small" />}
            onClick={() => setActiveTab(tabs[currentIndex + 1].id)}
          >
            suivant
          </Button>
        </span>
      </div>
    </div>
  );
};

interface ScheduleDeliveryTabsProps {
  order: Order;
  tabs: DeliveryFlowTab[];
  activeStep: DeliveryFlowStep;
  onStepChange: (step: DeliveryFlowStep) => void;
  isCoordonneesComplete: boolean;
  setIsCoordonneesComplete: (value: boolean) => void;
  hasReservation: boolean;
  reservation?: Reservation | null;
  officeConfig?: OfficeConfig | null;
  contractorConfig?: ContractorConfig | null;
  onReservationExpire?: () => void;
  onReservationUpdate?: (reservation: Reservation) => void;
  onCancelReservation?: () => Promise<void>;
  isCancellingReservation?: boolean;
}

interface ScheduleDeliveryTabPanelProps {
  children?: React.ReactNode;
  active: boolean;
}

const ScheduleDeliveryTabPanel = ({
  children,
  active,
}: ScheduleDeliveryTabPanelProps) => {
  return (
    <div className="schedule-delivery-tab-panel">
      {active && (
        <div className="schedule-delivery-tab-panel__content">{children}</div>
      )}
    </div>
  );
};

const ScheduleDeliveryTabs = ({
  order,
  tabs,
  activeStep,
  onStepChange,
  isCoordonneesComplete,
  setIsCoordonneesComplete,
  hasReservation,
  reservation,
  officeConfig,
  contractorConfig,
  onReservationExpire,
  onReservationUpdate,
  onCancelReservation,
  isCancellingReservation = false,
}: ScheduleDeliveryTabsProps) => {
  const coordonneesIndex = React.useMemo(() => {
    return tabs.findIndex((tab) => tab.id === DELIVERY_FLOW_STEP.coordonnees);
  }, [tabs]);

  const currentActiveIndex = React.useMemo(() => {
    return tabs.findIndex((tab) => tab.id === activeStep);
  }, [activeStep, tabs]);

  const isNextDisabled = React.useMemo(() => {
    if (currentActiveIndex >= coordonneesIndex && !isCoordonneesComplete) {
      return true;
    }

    return false;
  }, [currentActiveIndex, coordonneesIndex, isCoordonneesComplete]);

  const handleAdvanceToNextStep = React.useCallback(() => {
    const nextTab = tabs.find(
      (tab, index) => index > currentActiveIndex && !tab.disabled,
    );

    if (nextTab) {
      onStepChange(nextTab.id);
    }
  }, [currentActiveIndex, onStepChange, tabs]);

  const renderTabContent = (tabId: string) => {
    switch (tabId) {
      case DELIVERY_FLOW_STEP.reservation:
        if (!reservation) return null;
        if (reservation.status === "expired") {
          return <ReservationExpired />;
        }
        if (reservation.status === "cancelled") {
          return <ReservationCancelled />;
        }
        if (reservation.status === "used") {
          return <ReservationUsed legacy={true} reservation={reservation} />;
        }
        return (
          <ReservationDetails
            reservation={reservation}
            contractorConfig={contractorConfig}
            onExpire={onReservationExpire || (() => {})}
            onReservationUpdate={onReservationUpdate}
            onSaveSuccess={handleAdvanceToNextStep}
            onCancelReservation={onCancelReservation}
            isCancellingReservation={isCancellingReservation}
          />
        );
      case DELIVERY_FLOW_STEP.coordonnees:
        return (
          <ClientInformationForm
            client={order.client}
            setIsCoordonneesComplete={setIsCoordonneesComplete}
          />
        );
      case DELIVERY_FLOW_STEP.rdv:
        return (
          <Appointment
            order={order}
            onSubmitSuccess={handleAdvanceToNextStep}
          />
        );
      case DELIVERY_FLOW_STEP.recap:
        return (
          <OrderRecapDetails
            order={order}
            officeConfig={officeConfig || undefined}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="schedule-delivery-tabs">
      {tabs.map((tab, index) => (
        <ScheduleDeliveryTabPanel
          key={tab.id}
          active={currentActiveIndex === index}
        >
          {renderTabContent(tab.id)}
        </ScheduleDeliveryTabPanel>
      ))}
      <TabNavigation
        setActiveTab={onStepChange}
        currentIndex={currentActiveIndex}
        tabs={tabs}
        isNextDisabled={isNextDisabled}
      />
    </div>
  );
};

export default ScheduleDeliveryTabs;
